import { OrganizationResponseDto, organizationsQueryOptions } from "@/entities/organization";
import { SessionAction, SessionState, UserInfo, UserOrganization } from "@/entities/session";
import { sort } from "@/shared/utils/sort";
import { useQuery } from "@tanstack/react-query";
import { Dispatch, useCallback, useEffect, useMemo } from "react";


export const EMPTY_ORGANIZATION: UserOrganization = {
    organizationId: '',
    name: '',
    country: '',
    status: '',
    domain: '',
    managingOrganizationId: '',
    config: {},
    groups: [],
    roles: [],
    permissions: []
};

export const toUserOrganizations = (
    organizations: OrganizationResponseDto[],
    user: UserInfo | null
): UserOrganization[] => {
    const membershipList = user?.organizations?.organizationList;
    if (!membershipList) return [];

    const userOrganizations = membershipList
        .filter((membership) =>
            organizations.some(org => org.organizationId === membership.organizationId)
        )
        .map((membership) => {
            const matchedOrg = organizations.find(
                org => org.organizationId === membership.organizationId
            )!;
            return {
                organizationId: membership.organizationId,
                name: matchedOrg.name,
                country: matchedOrg.country,
                status: matchedOrg.status,
                domain: matchedOrg.domain,
                managingOrganizationId: matchedOrg.managingOrganizationId,
                config: matchedOrg.config,
                groups: membership.groups,
                roles: membership.roles,
                permissions: membership.permissions,
            };
        })

    return sort({ 
        items: userOrganizations, 
        sortDescriptor: { column: 'organizationId', direction: 'ascending' } 
    })
};


export const toCurrentOrganization = (
    userOrganizations: UserOrganization[],
    selectedOrgId: string | undefined,
    emptyOrganization: UserOrganization
): UserOrganization => {
    return userOrganizations.find((org) => org.organizationId === selectedOrgId)
        ?? userOrganizations[0]
        ?? emptyOrganization;
};


export const useSessionOrganizationState = (
    state: SessionState,
    dispatch: Dispatch<SessionAction>
) => {
    const { data: organizations = [] } = useQuery({
        ...organizationsQueryOptions(),
        enabled: state.user !== null,
    });

    const userOrganizations = useMemo(
        () => toUserOrganizations(organizations, state.user),
        [organizations, state.user]
    );

    const currentOrganization = useMemo(
        () => toCurrentOrganization(userOrganizations, state.selectedOrgId, EMPTY_ORGANIZATION),
        [userOrganizations, state.selectedOrgId]
    );

    const switchOrganization = useCallback(
        (organizationId: string) => dispatch({ type: 'SWITCH_ORG', organizationId }),
        []
    );

    // select default org
    useEffect(() => {
        if (!userOrganizations.length || state.selectedOrgId) return;

        switchOrganization(userOrganizations[0].organizationId)
    }, [userOrganizations, state.selectedOrgId, switchOrganization]);

    return { userOrganizations, currentOrganization, switchOrganization };
};